import React, { useEffect, useState } from 'react';
import { CheckCircle, ArrowLeft, Download, Mail, Clock, CreditCard, Home } from 'lucide-react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';

const PagamentoSucesso: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [paymentData, setPaymentData] = useState<any>(null);

  useEffect(() => {
    // Primeiro, tentar pegar dados do location.state (vindo do PagamentoRetorno)
    if (location.state) {
      setPaymentData(location.state);
      console.log('Dados recebidos via state:', location.state);
      return;
    }

    // Fallback: capturar parâmetros da URL do Mercado Pago (caso acesse diretamente)
    const status = searchParams.get('status');
    const paymentId = searchParams.get('payment_id');
    const preferenceId = searchParams.get('preference_id');
    const externalReference = searchParams.get('external_reference');

    if (status === 'approved' && paymentId) {
      setPaymentData({
        status,
        paymentId,
        preferenceId,
        externalReference
      });

      // Log para debug
      console.log('Pagamento aprovado:', {
        status,
        paymentId,
        preferenceId,
        externalReference
      });
    }
  }, [searchParams, location.state]);

  const handleVoltarInicio = () => {
    navigate('/');
  };

  const handleDownloadComprovante = () => {
    // Aqui você pode implementar o download do comprovante
    alert('Funcionalidade de download será implementada em breve!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full">
        {/* Header de Sucesso */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            🎉 Compra Efetuada com Sucesso!
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Seu pagamento foi processado e aprovado
          </p>
          <p className="text-sm text-gray-500">
            Você já pode acessar sua conta e começar a usar o sistema
          </p>
        </div>

        {/* Card Principal */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="ml-4">
              <h2 className="text-2xl font-bold text-gray-900">Pagamento Confirmado</h2>
              <p className="text-gray-600">Sua assinatura está ativa</p>
            </div>
          </div>

          {paymentData && (
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CreditCard className="w-5 h-5 mr-2" />
                Detalhes da Transação
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full text-sm">
                      Aprovado
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">ID do Pagamento:</span>
                    <span className="font-medium text-gray-900 text-sm">
                      {paymentData.paymentId}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  {paymentData.preferenceId && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Referência:</span>
                      <span className="font-medium text-gray-900 text-sm">
                        {paymentData.preferenceId}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Data:</span>
                    <span className="font-medium text-gray-900 text-sm">
                      {new Date().toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Próximos Passos */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              Próximos Passos
            </h3>
            <ul className="space-y-2 text-blue-800">
              <li className="flex items-center">
                <CheckCircle className="w-4 h-4 text-blue-600 mr-2" />
                Faça login na sua conta
              </li>
              <li className="flex items-center">
                <CheckCircle className="w-4 h-4 text-blue-600 mr-2" />
                Configure sua empresa
              </li>
              <li className="flex items-center">
                <CheckCircle className="w-4 h-4 text-blue-600 mr-2" />
                Comece a usar todas as funcionalidades
              </li>
            </ul>
          </div>

          {/* Ações */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link
              to="/"
              className="flex justify-center items-center px-6 py-4 border border-transparent text-base font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
            >
              <Home className="w-5 h-5 mr-2" />
              Acessar Sistema
            </Link>
            
            <button
              onClick={handleDownloadComprovante}
              className="flex justify-center items-center px-6 py-4 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <Download className="w-5 h-5 mr-2" />
              Baixar Comprovante
            </button>
          </div>
        </div>

        {/* Informações Adicionais */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-start">
            <Mail className="w-6 h-6 text-blue-600 mr-3 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Confirmação por E-mail
              </h3>
              <p className="text-gray-600 mb-4">
                Enviamos um e-mail de confirmação com todos os detalhes da sua compra. 
                Verifique sua caixa de entrada e spam.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Dica:</strong> Guarde o comprovante de pagamento para seus registros.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PagamentoSucesso;
